import React from 'react';
import {
  CustomFieldExtensionSchema,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { TextField } from '@material-ui/core';

/**
 * Converts a stored ISO 8601 timestamp back into the `YYYY-MM-DDTHH:mm` shape a
 * native datetime-local input expects, rendered in the browser's local timezone.
 */
export function isoToLocalInput(iso?: string): string {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/**
 * Converts the `YYYY-MM-DDTHH:mm` value from a datetime-local input (interpreted
 * in the browser's local timezone) into a full ISO 8601 / RFC 3339 timestamp
 * with timezone, which is what the GitHub announcement-banner API requires.
 */
export function localInputToIso(local: string): string {
  if (!local) {
    return '';
  }
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return d.toISOString();
}

/**
 * A datetime picker whose stored value is a valid ISO 8601 timestamp (with
 * timezone), avoiding the bare `YYYY-MM-DDTHH:mm` value the default RJSF
 * `datetime` widget produces (which GitHub rejects as an invalid date-time).
 */
export function LocalDateTimePicker({
  onChange,
  formData,
  required,
  rawErrors,
  schema: { title, description },
}: FieldExtensionComponentProps<string>): JSX.Element {
  return (
    <TextField
      label={title}
      type="datetime-local"
      value={isoToLocalInput(formData)}
      onChange={e => onChange(localInputToIso(e.target.value))}
      helperText={description}
      required={required}
      error={!!rawErrors?.length}
      InputLabelProps={{ shrink: true }}
      variant="outlined"
      fullWidth
      margin="normal"
    />
  );
}

export const LocalDateTimePickerSchema: CustomFieldExtensionSchema = {
  returnValue: {
    type: 'string',
    title: 'ISO 8601 timestamp',
  },
};
